import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileText, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useDocumentGrants } from '@/hooks/useDocumentGrants';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DocumentRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  conversationId: string;
  onRequestSent: (documentIds: string[], message?: string) => Promise<void>;
}

interface ListingDocument {
  id: string;
  document_name: string;
  document_category: string;
  is_confidential: boolean;
}

const DocumentRequestModal: React.FC<DocumentRequestModalProps> = ({
  open,
  onOpenChange,
  listingId,
  conversationId,
  onRequestSent
}) => {
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { getGrantedDocumentIds } = useDocumentGrants(listingId);

  const grantedDocIds = getGrantedDocumentIds();

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open, listingId]);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('listing_documents')
        .select('id, document_name, document_category, is_confidential')
        .eq('listing_id', listingId)
        .eq('is_confidential', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setDocuments((data as any) || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleToggle = (docId: string) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSubmit = async () => {
    if (selectedDocs.length === 0) return;

    setLoading(true);
    try {
      await onRequestSent(selectedDocs, message);
      onOpenChange(false);
      setSelectedDocs([]);
      setMessage('');
    } catch (error) {
      console.error('Error sending request:', error);
    } finally {
      setLoading(false);
    }
  };

  const availableDocs = documents.filter(
    doc => !grantedDocIds.includes(doc.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Request Document Access
          </DialogTitle>
          <DialogDescription>
            Select the confidential documents you need access to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availableDocs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p>You already have access to all confidential documents</p>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[300px] pr-4">
                <div className="space-y-3">
                  {availableDocs.map(doc => (
                    <div
                      key={doc.id}
                      className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                    >
                      <Checkbox
                        id={doc.id}
                        checked={selectedDocs.includes(doc.id)}
                        onCheckedChange={() => handleToggle(doc.id)}
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor={doc.id}
                          className="cursor-pointer font-medium"
                        >
                          {doc.document_name || doc.document_category}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {doc.document_category}
                        </p>
                      </div>
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="space-y-2">
                <Label htmlFor="message">Add a message (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="I'm seriously interested in purchasing and need these documents to complete my evaluation..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  {message.length}/500 characters
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedDocs.length === 0 || loading}
          >
            {loading ? 'Sending...' : `Send Request (${selectedDocs.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentRequestModal;
